import { BrowserRouter, Routes, Route, Link, NavLink } from 'react-router-dom'
import { useState, useEffect, lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthProvider, useAuth } from './context/AuthContext'
import PrivateRoute from './components/layout/PrivateRoute'
import EmailVerificationRequired from './components/auth/EmailVerificationRequired'
import Footer from './components/layout/Footer'
import UserMenu from './components/layout/UserMenu'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from './services/firebase'
import './App.css'

// Lazy load pages for better performance (code splitting)
const Home = lazy(() => import('./pages/Home'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const Practice = lazy(() => import('./pages/Practice'))
const Analytics = lazy(() => import('./pages/Analytics'))
const Settings = lazy(() => import('./pages/Settings'))
const Leaderboard = lazy(() => import('./pages/Leaderboard'))
const About = lazy(() => import('./pages/About'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const Debug = lazy(() => import('./pages/Debug'))

// Import Firebase test (runs automatically in dev mode)
import './services/testFirebase'
import './utils/leaderboardDebug' // Debug utilities for leaderboard issues
import './utils/sessionDebug' // Session debugging utilities

// Expose Firebase to window for debugging
import { auth } from './services/firebase'
if (typeof window !== 'undefined') {
  (window as any).__firebaseDb = db;
  (window as any).__firebaseAuth = auth;
  console.log('🔧 Firebase debug access enabled: window.__firebaseDb, window.__firebaseAuth');
  console.log('🔧 Session debug: Run checkSessionBreakdown() in console');
  console.log('🔧 Consent check: Run checkConsentSettings() in console');
  console.log('🔧 Leaderboard test: Run testLeaderboard(7) for 7-day, testLeaderboard(30) for 30-day');
}

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    color: 'var(--text-color)'
  }}>
    Loading...
  </div>
)

function NavBar() {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const [username, setUsername] = useState<string>('');
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Fetch username and USA nationality check when user logs in
  useEffect(() => {
    if (currentUser) {
      // Set up real-time listener for user data including nationality
      const unsubscribe = onSnapshot(
        doc(db, 'users', currentUser.uid),
        (userDoc) => {
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUsername(data.username || currentUser.email?.split('@')[0] || 'User');
          } else {
            setUsername(currentUser.email?.split('@')[0] || 'User');
          }
        },
        (error) => {
          console.error('Error fetching user data:', error);
          setUsername(currentUser.email?.split('@')[0] || 'User');
        }
      );
      return () => unsubscribe();
    } else {
      setUsername('');
    }
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  return (
    <nav className="navbar">
      <Link to="/" className="nav-brand">CustomTypingCoach</Link>
      <div className="nav-links">
        {currentUser ? (
          <>
            <NavLink to="/practice">{t('nav.practice')}</NavLink>
            <NavLink to="/analytics">{t('nav.analytics')}</NavLink>
            <NavLink to="/leaderboard">{t('nav.leaderboard')}</NavLink>
            <NavLink to="/about">{t('nav.about')}</NavLink>
            <NavLink to="/settings" title="Settings" className="settings-icon">
              <img src="/assets/gear-icon.png" alt="Settings" width="18" height="18" />
            </NavLink>
            <button 
              className="user-email clickable" 
              onClick={() => setShowUserMenu(true)}
              title="User Settings"
            >
              {username}
            </button>
            <button onClick={handleLogout} className="logout-button">
              {t('nav.logout')}
            </button>
          </>
        ) : (
          <>
            <NavLink to="/">{t('nav.home')}</NavLink>
            <NavLink to="/practice">{t('nav.practice')}</NavLink>
            <NavLink to="/about">{t('nav.about')}</NavLink>
            <NavLink to="/login">{t('nav.login')}</NavLink>
            <NavLink to="/register">{t('nav.register')}</NavLink>
          </>
        )}
      </div>
      
      {showUserMenu && (
        <UserMenu 
          username={username} 
          onClose={() => setShowUserMenu(false)} 
        />
      )}
    </nav>
  );
}

function AppContent() {
  const { currentUser } = useAuth();
  const skipVerification = import.meta.env.VITE_SKIP_EMAIL_VERIFICATION === 'true';

  // If user is logged in but email not verified, show verification screen
  // (unless verification is disabled for development)
  if (currentUser && !currentUser.emailVerified && !skipVerification) {
    return <EmailVerificationRequired />;
  }

  return (
    <div className="app">
      <NavBar />
      
      <main className="main-content">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Practice allows guest access - no auth required */}
            <Route path="/practice" element={<Practice />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/debug" element={<Debug />} />
            {/* These routes require authentication */}
            <Route path="/analytics" element={<PrivateRoute><Analytics /></PrivateRoute>} />
            <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
          </Routes>
        </Suspense>
      </main>
      
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
